import { useEffect, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { getStripeEnvironment } from "@/lib/stripe";

export type SubscriptionRow = {
  id: string;
  user_id: string;
  stripe_customer_id: string;
  stripe_subscription_id: string;
  product_id: string;
  price_id: string;
  status: string;
  current_period_end: string | null;
  cancel_at_period_end: boolean;
  environment: string;
  pending_tier: string | null;
  pending_effective_at: string | null;
  stripe_schedule_id: string | null;
};

/**
 * Current Stripe subscription for the signed-in user. Cached via TanStack
 * Query so multiple consumers (TrialBanner, PastDueBanner, settings cards,
 * WorkspaceProvider) share one network request and survive navigation.
 */
export function useSubscription() {
  const { user } = useAuth();
  const userId = user?.id ?? null;
  const env = getStripeEnvironment();
  const qc = useQueryClient();
  const queryKey = useMemo(() => ["subscription", userId, env] as const, [userId, env]);

  const { data: subscription = null, isLoading, refetch } = useQuery({
    queryKey,
    enabled: !!userId,
    staleTime: 60_000,
    gcTime: 30 * 60_000,
    queryFn: async (): Promise<SubscriptionRow | null> => {
      const { data, error } = await supabase
        .from("subscriptions")
        .select("*")
        .eq("user_id", userId!)
        .eq("environment", env)
        .order("updated_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      return (data as unknown as SubscriptionRow) ?? null;
    },
  });

  // Refetch when the user returns to the tab (e.g. after Stripe Customer Portal).
  useEffect(() => {
    if (!userId) return;
    const onFocus = () => qc.invalidateQueries({ queryKey });
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [userId, qc, queryKey]);

  const periodEnd = subscription?.current_period_end ? new Date(subscription.current_period_end) : null;
  const periodEndInFuture = !periodEnd || periodEnd > new Date();

  const isActive =
    !!subscription &&
    (
      (["active", "trialing", "past_due"].includes(subscription.status) && periodEndInFuture) ||
      (subscription.status === "canceled" && !!periodEnd && periodEnd > new Date())
    );

  const isTrialing = !!subscription && subscription.status === "trialing" && periodEndInFuture;
  const isPastDue = !!subscription && subscription.status === "past_due";

  const trialDaysRemaining = isTrialing && periodEnd
    ? Math.max(0, Math.ceil((periodEnd.getTime() - Date.now()) / 86_400_000))
    : null;
  const trialEndsAt = isTrialing ? periodEnd : null;

  const pendingTier = subscription?.pending_tier ?? null;
  const pendingEffectiveAt = subscription?.pending_effective_at
    ? new Date(subscription.pending_effective_at)
    : null;
  const hasPendingDowngrade = !!pendingTier && !!pendingEffectiveAt && pendingEffectiveAt > new Date();

  return {
    subscription,
    loading: !!userId && isLoading,
    isActive,
    isTrialing,
    isPastDue,
    trialDaysRemaining,
    trialEndsAt,
    pendingTier,
    pendingEffectiveAt,
    hasPendingDowngrade,
    refresh: async () => { await refetch(); },
  };
}
